import { getAppsGatewayUrl } from '../../../../../../../constants/env';
import { WORKFLOW_IDS } from './constants';
import type {
  FetchInvokeRequest,
  FetchPetFormValues,
  FetchQuoteResult,
} from './types';

interface FetchQuoteParams {
  jwt: string;
  sessionId: string;
  retailerMoniker: string;
  orderNumber?: string;
  trackingNumber?: string;
  device?: 'mobile' | 'desktop';
  locale?: string;
  trackingStatus?: string;
  pet: FetchPetFormValues;
}

// The one quote failure a shopper can act on; everything else is noise to them.
const UNQUOTABLE_ADDRESS = 'UNQUOTABLE_ADDRESS';

export type FetchQuoteFailure = 'address' | 'unknown';

export type FetchQuoteOutcome =
  | { ok: true; quote: FetchQuoteResult }
  | { ok: false; reason: FetchQuoteFailure };

export async function fetchQuote(
  params: FetchQuoteParams,
): Promise<FetchQuoteOutcome> {
  const {
    jwt,
    sessionId,
    retailerMoniker,
    orderNumber,
    trackingNumber,
    device,
    locale,
    trackingStatus,
    pet,
  } = params;

  const invokeRequest: FetchInvokeRequest = {
    workflowId: WORKFLOW_IDS.FETCH_QUOTE,
    context: {
      sessionId,
      retailerMoniker,
      orderNumber,
      trackingNumber,
      device,
      locale,
      trackingStatus,
    },
    body: {
      species: pet.species,
      breedId: pet.breedId,
      age: pet.age,
      petName: pet.petName,
      email: pet.email,
      zip: pet.zip,
    },
  };

  try {
    const gatewayUrl = getAppsGatewayUrl();
    const res = await fetch(`${gatewayUrl}/invoke`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify(invokeRequest),
    });

    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as {
        error?: { code?: string };
      } | null;
      return {
        ok: false,
        reason:
          body?.error?.code === UNQUOTABLE_ADDRESS ? 'address' : 'unknown',
      };
    }

    const { data } = (await res.json()) as { data?: FetchQuoteResult | null };
    if (!data) return { ok: false, reason: 'unknown' };
    return { ok: true, quote: data };
  } catch (err) {
    console.error('Failed to fetch Fetch quote:', err);
    return { ok: false, reason: 'unknown' };
  }
}
